import type { ScreenShape } from "./ScreenShape";
import { resolveResponsive, type Responsive } from "./Responsive";
import { rectHeight, rectWidth, type DesignRect } from "./ContentArea";

/** The limits a fitted box honours for one shape; an absent limit means "the available area". */
export interface FitLimits {
  maxWidth?: number;
  maxHeight?: number;
  /** The reference height the fit uses instead of the box's own (the landscape enlargement rule). */
  referenceHeight?: number;
}

/** The box a fit places, in the node's own units: its size and, when not centred on the node's origin, its centre. */
export interface FitBox {
  width: number;
  height: number;
  centerX?: number;
  centerY?: number;
}

/** A per-shape fit rule: the box, the limits, an optional per-shape offset (y-down) from the area's centre. */
export interface FitRule {
  box: Responsive<FitBox>;
  limits: Responsive<FitLimits>;
  offsetY?: Responsive<number>;
}

/**
 * The uniform scale that fits `reference` into `available` under `limits` (the scale-mode window
 * layout: the largest scale at which the box stays inside both the limits and the area).
 */
export function fitScale(reference: { width: number; height: number }, limits: FitLimits, available: { width: number; height: number }): number {
  const refHeight = limits.referenceHeight && limits.referenceHeight > 0 ? limits.referenceHeight : reference.height;
  const allowedWidth = Math.min(limits.maxWidth ?? Number.POSITIVE_INFINITY, available.width > 0 ? available.width : Number.POSITIVE_INFINITY);
  const allowedHeight = Math.min(limits.maxHeight ?? Number.POSITIVE_INFINITY, available.height > 0 ? available.height : Number.POSITIVE_INFINITY);
  const scale = Math.min(allowedWidth / reference.width, allowedHeight / refHeight);
  return Number.isFinite(scale) && scale > 0 ? scale : 1;
}

/**
 * Fits the shape's box into `area` (the content-centre fit): the scale from `fitScale`, and the node
 * position that puts the box's centre on the area's centre, plus the shape's offset.
 */
export function fitWithin(rule: FitRule, shape: ScreenShape, area: DesignRect): { scale: number; x: number; y: number } {
  const box = resolveResponsive(rule.box, shape);
  const scale = fitScale(box, resolveResponsive(rule.limits, shape), { width: rectWidth(area), height: rectHeight(area) });
  const offsetY = resolveResponsive(rule.offsetY ?? 0, shape);
  return {
    scale,
    x: (area.left + area.right) / 2 - (box.centerX ?? 0) * scale,
    y: (area.top + area.bottom) / 2 + offsetY - (box.centerY ?? 0) * scale,
  };
}
